import { useState, type JSX } from "react";
import { KeyRound, LogOut, ListTodo, ArrowRight, UsersRound, Factory, CalendarClock, CircleDollarSign, Bot } from "lucide-react";
import { hatRecht } from "../../state/berechtigungen.ts";
import type { Nutzer } from "../../state/reduzierer.ts";
import { SitzungsFehler } from "../../api/basis.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { relativeZeitangabe, zeitpunktText, zuletztAktive } from "../../lib/zuletztAktiv.ts";
import { rollenBezeichnung } from "../Users/RollenAuswahl.tsx";
import { ThemeSchalter } from "./ThemeSchalter.tsx";

export interface MarkenMenueProps {
  nutzer: { displayName: string; username: string; role: string } | null;
  rechte: ReadonlySet<string>;
  /** `openPasswordDialog()` (index.html:5737-5744), Knopf "Passwort". */
  onPasswort: () => void;
  /** `logout()` (index.html:5726-5735), Knopf "Abmelden". */
  onAbmelden: () => void;
  /**
   * `openManagedUser(id)`/`openUserManagement()` (index.html:5445-5455): ein Klick auf ein Konto der
   * Liste ruft mit dessen id, der Fussknopf "Nutzerverwaltung öffnen" mit `null`
   * (`selectedManagedUserId = null`).
   */
  onNutzerverwaltung: (id: string | null) => void;
  /**
   * Oeffnet die Lieferantenflaeche (`App.tsx#lieferantenOeffnen` -> `setSeite("suppliers")`).
   *
   * OHNE VORBILD IM BESTAND und ohne Rechteweiche - die zwei Saetze gehoeren zusammen:
   *
   *  - Der Eintrag ist die Entscheidung des Auftraggebers vom 2026-08-11 (Fix-Runde 1 zu Task 11):
   *    die Lieferantenflaeche gehoert NICHT in die Hauptnavigation, sondern hierher, wo
   *    Nutzerverwaltung und Aufgabenvorlagen schon liegen. Sie ist Verwaltung, keine taegliche
   *    Arbeitsansicht.
   *  - PFLICHT-Prop, nicht optional: `MarkenMenue` zeichnet den Knopf fuer JEDES angemeldete Konto
   *    (Global Constraint 9, "Lesen steht allen Rollen offen" - `GET /api/suppliers/:kLieferant`
   *    traegt `null`-Rechte, `backend/src/http/endpunktMatrix.ts:105`). Ein optionaler Rueckruf mit
   *    No-op-Vorgabe waere hier also ein sichtbarer Knopf, der bei einem vergessenen Einhaengen
   *    STILL nichts taete - genau die Fehlerklasse, die `test/reference/bundle.test.ts` unter
   *    "SICHTBARE Bedienelemente ohne Wirkung" fuehrt.
   */
  onLieferanten: () => void;
  /**
   * Oeffnet die Seite "Zahlungsvorlagen" (`App.tsx#zahlungsvorlagenOeffnen`). Ohne Rechteweiche wie
   * `onLieferanten` (Lesen steht allen Rollen offen, Schreiben sperrt die Seite selbst) und aus
   * demselben Grund PFLICHT-Prop: ein sichtbarer Knopf darf bei vergessener Verdrahtung nicht still
   * nichts tun. Der Eintrag ist auf ausdruecklichen Wunsch des Nutzers (2026-10-01) eine eigene,
   * auffindbare Seite und steht nicht in `index.html` - siehe `SIGNATUR_AUSNAHMEN`.
   */
  onZahlungsvorlagen: () => void;
  /**
   * Oeffnet "Meine KI-Zugänge" (`App.tsx#kiZugaengeOeffnen`): die KI-Werkzeuge (ChatGPT, Claude), die das Konto
   * verbunden hat, samt Widerruf. Ohne Rechteweiche wie `onLieferanten` und `onZahlungsvorlagen` - jedes Konto
   * verwaltet seine eigenen Zugaenge, nur "Alle Verbindungen" auf der Seite haengt an `manage_users` - und aus
   * demselben Grund PFLICHT-Prop: ein sichtbarer Knopf darf bei vergessener Verdrahtung nicht still nichts tun.
   * Ohne Vorbild im Bestand, siehe `SIGNATUR_AUSNAHMEN`.
   */
  onKiZugaenge: () => void;
  /**
   * Ist der KI-Zugang im Bestellhub eingeschaltet (`kiZugangAktiv` aus `GET /api/auth/me`)? Nur `true` zeichnet den
   * Eintrag "Meine KI-Zugänge": bei abgeschaltetem Schalter (`MCP_AKTIV` aus) soll der Rollout im Hub nichts veraendern.
   * Weggelassen = `false` (aelteres Backend ohne das Feld). Die Seiten selbst bleiben per Adresse erreichbar und melden
   * dann, dass der KI-Zugang nicht eingeschaltet ist.
   */
  kiZugangAktiv?: boolean;
  /** Pflicht, weil `manage_fx` den sichtbaren USD-Konten-Zugang freischaltet. */
  onUsdKonten: () => void;
  /**
   * Oeffnet "Task-Template verwalten" (`App.tsx#taskTemplateOeffnen` -> `<VorlagenDialog>`),
   * Herkunft `openTaskTemplateManager()` (index.html:5510-5524).
   *
   * PFLICHT-Prop aus demselben Grund wie `onLieferanten` darueber: der Knopf wird fuer jedes Konto
   * mit `manage_templates` GEZEICHNET und ist nicht gesperrt. Ein optionaler Rueckruf mit
   * No-op-Vorgabe waere hier also ein sichtbares Bedienelement, das bei einem vergessenen
   * Einhaengen STILL nichts taete - genau die Fehlerklasse, die `test/reference/bundle.test.ts`
   * unter "SICHTBARE Bedienelemente ohne Wirkung" fuehrt. Bis zum 2026-08-12 war der Knopf genau
   * das (siehe den Absatz im Kopfkommentar unten).
   */
  onTaskTemplate: () => void;
  /**
   * `loadBrandUsers()` (index.html:5409-5432) - laedt beim OEFFNEN des Menues, NUR mit
   * `manage_users` (`toggleBrandMenu`, index.html:5442-5444: `if (hasPermission('manage_users'))
   * loadBrandUsers();`). Optional: `kopfzeile.test.tsx` (bleibt unberuehrt) uebergibt `nutzer={null}`
   * an `<Kopfzeile>` und braucht deshalb keine Attrappe - diese Komponente rendert dort ohnehin
   * `null` (siehe Kopfkommentar unten), bevor `nutzerLaden` je gelesen wuerde.
   */
  nutzerLaden?: () => Promise<Nutzer[]>;
}

/**
 * `brandInitials(user)` (index.html:5403-5407): die ersten Buchstaben von Vor- UND Nachnamen (nach
 * Leerraum getrennt), sonst die ersten zwei Zeichen von Anzeigename/Benutzername.
 */
function initialen(konto: { displayName?: string; username?: string }): string {
  const quelle = String(konto.displayName || konto.username || "").trim();
  const teile = quelle.split(/\s+/).filter(Boolean);
  const kurz = teile.length > 1 ? teile[0]![0]! + teile.at(-1)![0]! : quelle.slice(0, 2);
  return kurz.toUpperCase();
}

/**
 * `.brand-access` (index.html:3636-3669): Trigger, Theme-Umschalter, Konto-Panel, Administration.
 *
 * Bei `nutzer === null` wird NICHTS gerendert. Im Original bleibt der Trigger auch ohne
 * angemeldeten Nutzer im DOM (nur `aria-disabled`/`tabIndex=-1`, index.html:5376-5379) - dieser Fall
 * tritt in der SPA aber praktisch nie ein: `App.tsx` zeigt `<Kopfzeile>` erst nach erfolgreichem
 * Login, `nutzer` ist dort also nie `null`. Die Abweichung ist hier nötig, damit
 * `kopfzeile.test.tsx` (Task-Brief, Schritt 2 - immer mit `nutzer={null}` aufgerufen) exakt sechs
 * Buttons zählt: ein wörtlich nachgebauter Trigger-Button (nur ein `<img>`-Kind, leerer
 * `textContent`) wäre ein siebter, nicht auf die vorgegebenen sechs Beschriftungen abbildbarer
 * Eintrag. Der optische/DOM-Vergleich (Task-Brief, Schritt 6) meldet sich vor der Aufnahme immer an
 * (`anmelden(page)`) - dort ist `nutzer` gesetzt, die volle Struktur unten entsteht also genau dort,
 * wo sie gegen die `index.html`-Baseline geprüft wird.
 *
 * "Task-Template verwalten" hatte von Task 10 bis zum 2026-08-12 KEIN Klickverhalten (kein
 * `onClick`) - als BENANNTE Lücke geführt, hier wie in `test/reference/bundle.test.ts`, weil die
 * Fläche dahinter noch nicht gebaut war. Der Auftraggeber hat gemeldet, dass der Knopf nichts tut;
 * die Fläche ist seither `components/TaskTemplate/VorlagenDialog.tsx`, der Rückruf heißt
 * `onTaskTemplate` (oben). Die Lücke ist damit geschlossen und aus beiden Kommentaren gestrichen.
 * "Passwort", "Abmelden" und "Nutzerverwaltung öffnen" (samt der Kontoliste) sind seit Task 10
 * verdrahtet - siehe `onPasswort`/`onAbmelden`/`onNutzerverwaltung`/`nutzerLaden` oben.
 *
 * "Lieferanten öffnen" ist seit der Fix-Runde 1 zu Task 11 (2026-08-11) der DRITTE Fußknopf und der
 * einzige Eintrag dieses Menüs OHNE Vorbild im Bestand: der Auftraggeber hat entschieden, dass die
 * Lieferantenfläche hierher gehört und nicht in die Hauptnavigation. Siehe `onLieferanten` oben und
 * den Kommentar am Knopf selbst.
 * "Zuletzt aktiv" (Auftrag "Letzter Login", 2026-10-18) ersetzt die Kontoliste "Nutzerkonten": der
 * Bestand (`loadBrandUsers`, index.html:5409-5432) listet ALLE Konten und markiert mit dem grünen
 * Punkt nur `active` - also "Konto aktiviert", nicht "gerade da"; deshalb waren alle grün. Jetzt
 * erscheinen nur Konten mit `lastSeenAt` in den letzten 24 Stunden (`lib/zuletztAktiv.ts`), mit
 * relativer Zeitangabe statt Punkt. Eine bewusste Abweichung vom Bestand: der Kopftext "Nutzerkonten"
 * und der zusätzliche Hinweis "letzte 24 Std." stehen in den Kopfzeilen-Signaturen der Playwright-
 * Baselines (`test/visual/baseline/*`, nicht Teil von `vitest run`) anders. Die Baselines bleiben
 * Altbestand: sie werden immer aus `index.html` aufgenommen (`test/visual/baseline-aufnehmen.spec.ts`),
 * eine Neuaufnahme lieferte wieder "Nutzerkonten". Kosten und Nachweis stehen im Eintrag
 * "Nutzer/LetzterLogin" in `test/visual/ausnahmen.ts`.
 * Für die Kopfzeilen-Baseline (Schritt 6, Task 7) zählt nur die Struktur im geschlossenen Zustand,
 * die Buttons sind darüber hinaus vollständig markiert (Klassen, ids, Icons, Beschriftungen).
 */
export function MarkenMenue({
  nutzer,
  rechte,
  onPasswort,
  onAbmelden,
  onNutzerverwaltung,
  onLieferanten,
  onZahlungsvorlagen,
  onKiZugaenge,
  kiZugangAktiv = false,
  onUsdKonten,
  onTaskTemplate,
  nutzerLaden,
}: MarkenMenueProps): JSX.Element | null {
  const [offen, setOffen] = useState(false);
  // `null` = noch nicht geladen (Anfangszustand JEDES Oeffnens, `loadBrandUsers`s erste Zeile setzt
  // wieder auf "Konten werden geladen…" zurueck), danach entweder die Liste oder ein Fehlertext.
  const [kontoListe, setKontoListe] = useState<readonly Nutzer[] | null>(null);
  const [kontoFehler, setKontoFehler] = useState<string | null>(null);
  if (!nutzer) return null;

  // Die Uhr wird EINMAL je Zeichnen gelesen und an die reinen Funktionen in `lib/zuletztAktiv.ts`
  // uebergeben - nicht tief im JSX. Das Menue laedt die Liste bei jedem Oeffnen neu (siehe
  // `menueUmschalten`), die relative Angabe ist damit hoechstens so alt wie das Oeffnen.
  const jetzt = Date.now();
  const aktiveKonten = kontoListe === null ? [] : zuletztAktive(kontoListe, jetzt);

  const kannNutzerVerwalten = hatRecht(rechte, "manage_users");
  const kannVorlagenVerwalten = hatRecht(rechte, "manage_templates");
  const kannFxVerwalten = hatRecht(rechte, "manage_fx");

  /**
   * `toggleBrandMenu(event)` (index.html:5438-5444): schliesst nur, wenn bereits offen, sonst
   * oeffnet es UND laedt - `if (hasPermission('manage_users')) loadBrandUsers();` - NUR beim
   * Uebergang zu/nach offen, nicht bei jedem erneuten Zeichnen.
   *
   * Minor 2 (Fix-Runde 1, Task 10) - `nutzerLaden()`/`setKontoListe`/`setKontoFehler` liefen vorher
   * INNERHALB der an `setOffen` uebergebenen Updater-Funktion. React ruft Updater-Funktionen unter
   * `StrictMode` (produktiver Einstieg, `main.tsx`) zur Entwicklungszeit ABSICHTLICH zweimal auf, um
   * Nicht-Idempotenz aufzudecken ("Functions passed to useState ..." laut React-Doku zu StrictMode).
   * GEMESSEN, NICHT ANGENOMMEN: `test/komponenten/App/verdrahtungNutzer.test.tsx`, Fall (f) - ein
   * `render(...)` in `<StrictMode>` zeigte VOR dieser Aenderung ZWEI `nutzerLaden`-Aufrufe pro Klick
   * (ein zweiter, ungewollter `GET /api/users`), danach genau einen. Jetzt reines Lesen im Updater
   * (`!offen`), die Nebenwirkung DANEBEN statt DARIN.
   */
  function menueUmschalten(): void {
    const naechster = !offen;
    setOffen(naechster);
    if (naechster && kannNutzerVerwalten && nutzerLaden) {
      setKontoListe(null);
      setKontoFehler(null);
      nutzerLaden()
        .then((liste) => setKontoListe(liste))
        .catch((fehler: unknown) => {
          // Nit (Fix-Runde 1, Task 10): der Bestand (`loadBrandUsers`, index.html:5431) unterdrueckt
          // 'unauthorized' bewusst - `api()` wirft dort literal `new Error('unauthorized')` bei
          // jedem 401 (index.html:5312-5323) und hat zu dem Zeitpunkt bereits `showLogin()`
          // aufgerufen; ein zusaetzlicher Fehlertext im Menue waere irrefuehrend. Der Aequivalent-Typ
          // hier ist `SitzungsFehler` (`api/basis.ts`, geworfen bei jedem 401 durch `anfrage()`),
          // NICHT der Nachrichtentext - der traegt in diesem Port die Server-Fehlermeldung, nie
          // woertlich "unauthorized".
          if (fehler instanceof SitzungsFehler) return;
          // R2: die Meldung bleibt INLINE im Menue; R1: der Text kommt vom Uebersetzer, nie die rohe `.message`.
          const kontext = "Konten konnten nicht geladen werden.";
          setKontoFehler(fehlertext(fehler, kontext) ?? kontext);
        });
    }
  }

  /** `closeBrandMenu()` (index.html:3620-3626) vor jeder der drei Aktionen unten. */
  function geschlossenDann(aktion: () => void): () => void {
    return () => {
      setOffen(false);
      aktion();
    };
  }

  return (
    <div className="brand-access" id="brandAccess">
      <button
        // `admin-enabled`/`tabIndex`/`aria-disabled` (index.html:5377-5379, `updateUserUi`) hängen
        // dort an `loggedIn` - hier unbedingt gesetzt, weil `MarkenMenue` ohnehin nur bei
        // vorhandenem `nutzer` rendert (siehe Kopfkommentar oben, "Bei nutzer === null...").
        className="brand-trigger admin-enabled"
        id="brandMenuTrigger"
        type="button"
        aria-label="ABSINA-Menü"
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-disabled="false"
        tabIndex={0}
        onClick={menueUmschalten}
      >
        <img src="ABSINA_Logo_Schwarz_1500x287px_300dpi.png" alt="ABSINA" className="logo" />
      </button>
      <div className={offen ? "brand-admin-menu open" : "brand-admin-menu"} id="brandAdminMenu" role="menu" aria-hidden={!offen}>
        <ThemeSchalter />
        <div className="brand-account-panel" id="brandAccountPanel">
          <div className="brand-account-copy">
            <strong id="brandAccountName">{nutzer.displayName}</strong>
            <span id="brandAccountRole">{`${nutzer.username} · ${rollenBezeichnung(nutzer.role)}`}</span>
          </div>
          <div className="brand-account-actions">
            <button type="button" onClick={geschlossenDann(onPasswort)}>
              <KeyRound aria-hidden="true" strokeWidth={1.8} />
              Passwort
            </button>
            <button type="button" onClick={geschlossenDann(onAbmelden)}>
              <LogOut aria-hidden="true" strokeWidth={1.8} />
              Abmelden
            </button>
          </div>
          <span className="save-state brand-account-save" id="saveState" />
        </div>
        {/*
          `#brandAdminSection` haengt seit der Fix-Runde 1 zu Task 11 UNBEDINGT - vorher nur bei
          `manage_users || manage_templates`. Zwei Gruende, beide tragend:

          1. Der Lieferanten-Eintrag unten haengt an KEINEM Recht (siehe `onLieferanten` oben). Bliebe
             die Bedingung stehen, saehe ein Konto ohne `manage_users`/`manage_templates` den einzigen
             Zugang zur Lieferantenflaeche nicht - obwohl das Lesen ihm ausdruecklich offensteht.
          2. Der Bestand fuehrt den Behaelter ebenfalls unbedingt (`index.html:3660`, statisches
             Markup); die Bedingung war die Abweichung, nicht ihre Ruecknahme.
        */}
        <div id="brandAdminSection">
          {kannNutzerVerwalten && (
            <div className="brand-menu-head" id="brandUsersHead">
              <div className="brand-menu-kicker">Administration</div>
              <div className="brand-menu-title">Zuletzt aktiv</div>
              <div className="brand-menu-note">letzte 24 Std.</div>
            </div>
          )}
          {kannNutzerVerwalten && (
            <div className="brand-user-list" id="brandUserList">
              {kontoFehler ? (
                <div className="brand-menu-empty">{kontoFehler}</div>
              ) : kontoListe === null ? (
                <div className="brand-menu-loading">Konten werden geladen…</div>
              ) : aktiveKonten.length === 0 ? (
                <div className="brand-menu-empty">In den letzten 24 Stunden war niemand aktiv.</div>
              ) : (
                aktiveKonten.map((konto) => (
                  <button
                    key={konto.id}
                    className="brand-user-option"
                    type="button"
                    role="menuitem"
                    onClick={geschlossenDann(() => onNutzerverwaltung(konto.id))}
                  >
                    <span className="brand-user-avatar">{initialen(konto)}</span>
                    <span className="brand-user-copy">
                      <span className="brand-user-display">{konto.displayName}</span>
                      <span className="brand-user-login">{`${konto.username} · ${rollenBezeichnung(konto.role)}`}</span>
                    </span>
                    <span className="brand-user-seen" title={zeitpunktText(konto.lastSeenAt)}>
                      {relativeZeitangabe(konto.lastSeenAt, jetzt)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
          {kannVorlagenVerwalten && (
            <button
              className="brand-menu-footer brand-task-template"
              id="brandTemplateButton"
              type="button"
              onClick={geschlossenDann(onTaskTemplate)}
            >
              <span className="icon-label">
                <ListTodo aria-hidden="true" strokeWidth={1.8} /> Task-Template verwalten
              </span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </button>
          )}
          {kannNutzerVerwalten && (
            <button
              className="brand-menu-footer"
              id="brandUsersButton"
              type="button"
              onClick={geschlossenDann(() => onNutzerverwaltung(null))}
            >
              <span className="icon-label">
                <UsersRound aria-hidden="true" strokeWidth={1.8} /> Nutzerverwaltung öffnen
              </span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </button>
          )}
          {kannFxVerwalten ? (
            <button
              className="brand-menu-footer"
              id="brandFxAccountsButton"
              type="button"
              onClick={geschlossenDann(onUsdKonten)}
            >
              <span className="icon-label">
                <CircleDollarSign aria-hidden="true" strokeWidth={1.8} /> USD-Konten verwalten
              </span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </button>
          ) : null}
          {/*
            DER LIEFERANTEN-ZUGANG (Entscheidung des Auftraggebers, 2026-08-11 - Fix-Runde 1 zu
            Task 11). Bewusst hier und bewusst so:

            1. ALS LETZTER EINTRAG. "Task-Template verwalten" und "Nutzerverwaltung öffnen" stehen
               wörtlich in dieser Reihenfolge im Bestand (`index.html:3665-3666`); ein Einschub
               dazwischen wäre ein Redesign des Bestands, ein Anhängen ist eine Ergänzung.
            2. OHNE RECHTEWEICHE - anders als seine beiden Nachbarn. Das ist kein Vergessen,
               sondern Global Constraint 9 ("Lesen steht allen Rollen offen"), deckungsgleich mit
               der gemessenen Endpunktmatrix: `GET /api/suppliers/:kLieferant` trägt `null`-Rechte
               (`backend/src/http/endpunktMatrix.ts:105`). Nur das SCHREIBEN hängt an
               `manage_suppliers`, und das sperrt `Suppliers/ExtrasFormular.tsx`, nicht dieser
               Knopf.
            3. `geschlossenDann(...)` wie bei allen anderen Aktionen dieses Menüs
               (`closeBrandMenu()`, index.html:3620-3626): das Menü schließt sich vor dem
               Seitenwechsel, sonst bliebe es über der neuen Fläche offen stehen.

            FOLGE FÜR DEN GANZSEITEN-VERGLEICH, gemessen und nicht vermutet (siehe Fix-Runden-
            Bericht, Abschnitt „Bildvergleich"): dieser Knopf steht im `<header>`-Teilbaum und damit
            in JEDER Kopfzeilen-SIGNATUR - die Baseline stammt aus `index.html`, das ihn nie hatte.
            Auf das BILD wirkt er nicht: `.brand-admin-menu` ist geschlossen `position: absolute` mit
            `visibility: hidden` (index.html:3679-3687 im Stilblock), belegt also keine Fläche in der
            Kopfzeile. Genau darin unterscheidet sich dieser Ort vom sechsten Reiter, den er ersetzt.
          */}
          <button
            className="brand-menu-footer"
            id="brandSuppliersButton"
            type="button"
            onClick={geschlossenDann(onLieferanten)}
          >
            <span className="icon-label">
              <Factory aria-hidden="true" strokeWidth={1.8} /> Lieferanten öffnen
            </span>
            <ArrowRight aria-hidden="true" strokeWidth={1.8} />
          </button>
          {/*
            ZAHLUNGSVORLAGEN (Nutzerwunsch 2026-10-01: eine eigene, auffindbare Seite). Wie der
            Lieferanten-Eintrag OHNE Rechteweiche (Lesen steht allen Rollen offen), direkt darunter und
            ebenfalls ohne Vorbild im Bestand: die Baseline-Signatur der Kopfzeile kann diesen Knoten
            nicht enthalten, er steht deshalb in `test/visual/signatur.ts#SIGNATUR_AUSNAHMEN`. Bild
            unveraendert: das Menue ist geschlossen `visibility: hidden` und belegt keine Flaeche.
          */}
          <button
            className="brand-menu-footer"
            id="brandPaymentTemplatesButton"
            type="button"
            onClick={geschlossenDann(onZahlungsvorlagen)}
          >
            <span className="icon-label">
              <CalendarClock aria-hidden="true" strokeWidth={1.8} /> Zahlungsvorlagen öffnen
            </span>
            <ArrowRight aria-hidden="true" strokeWidth={1.8} />
          </button>
          {/*
            MEINE KI-ZUGAENGE (KI-Zugang, Stufe 1): die Verbindungen des Kontos zu KI-Werkzeugen, mit Widerruf. Wie die
            beiden Eintraege darueber OHNE Rechteweiche (jedes Konto sieht und widerruft seine eigenen), als letzter
            Eintrag und ohne Vorbild im Bestand: die Baseline-Signatur der Kopfzeile kann diesen Knoten nicht
            enthalten, er steht deshalb in `test/visual/signatur.ts#SIGNATUR_AUSNAHMEN`. Bild unveraendert: das Menue
            ist geschlossen `visibility: hidden` und belegt keine Flaeche. NUR bei eingeschaltetem KI-Zugang
            (`kiZugangAktiv`, siehe Prop): bei `MCP_AKTIV` aus aendert sich im Menue nichts.
          */}
          {kiZugangAktiv ? (
            <button
              className="brand-menu-footer"
              id="brandKiZugaengeButton"
              type="button"
              onClick={geschlossenDann(onKiZugaenge)}
            >
              <span className="icon-label">
                <Bot aria-hidden="true" strokeWidth={1.8} /> Meine KI-Zugänge
              </span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
